import type { ChartConfig } from "@/components/ui/ChartTooltip";
import type { PieDonutDatum } from "@/components/ui/pie-chart/PieDonutChart";

/** Same palette as HorizontalBar — theme tokens only (no hex in specs). */
const PIE_CHART_SEQUENCE = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
] as const;

/**
 * Maps segment label to fill: sentiment CSS vars when the name matches, else
 * {@link PIE_CHART_SEQUENCE} by `paletteIndex` (only valid rows should increment the index).
 */
const colorForLabel = (label: string, paletteIndex: number): string => {
  const n = label.toLowerCase();
  if (n.includes("positive")) return "var(--sentiment-positive)";
  if (n.includes("negative")) return "var(--sentiment-negative)";
  if (n.includes("neutral")) return "var(--sentiment-neutral)";
  const idx = paletteIndex % PIE_CHART_SEQUENCE.length;
  return PIE_CHART_SEQUENCE[idx] as (typeof PIE_CHART_SEQUENCE)[number];
};

/**
 * Best-to-worst sentiment order for proportional breakdowns (matches catalog UI rules).
 */
const sentimentRank = (name: string): number => {
  const n = name.toLowerCase();
  if (n.includes("positive")) return 0;
  if (n.includes("neutral")) return 1;
  if (n.includes("negative")) return 2;
  return 3;
};

/**
 * Builds {@link ChartConfig} and rows for {@link PieDonutChart} from catalog or story props.
 * Sorts sentiment segments Positive → Neutral → Negative; non-sentiment rows keep stable order after.
 * Palette indices apply only to rows that are included (skipped invalid rows do not consume a slot).
 */
export const buildPieChartCatalogModel = (input: {
  data: Array<Record<string, unknown>>;
  nameKey: string;
  valueKey: string;
  /** Optional segment name → hex overrides (Layout Studio). */
  colors?: Record<string, string> | null;
}): { chartConfig: ChartConfig; chartData: PieDonutDatum[] } => {
  const { data, nameKey, valueKey, colors } = input;
  const valueLabel =
    valueKey.length > 0
      ? `${valueKey.charAt(0).toUpperCase()}${valueKey.slice(1)}`
      : valueKey;

  const chartConfig: ChartConfig = {
    [valueKey]: { label: valueLabel },
  };
  const sorted = [...data].sort((a, b) => {
    const an = String(a[nameKey] ?? "");
    const bn = String(b[nameKey] ?? "");
    const ra = sentimentRank(an);
    const rb = sentimentRank(bn);
    if (ra !== rb) return ra - rb;
    return an.localeCompare(bn);
  });

  const chartData: PieDonutDatum[] = [];
  let paletteIndex = 0;

  for (const row of sorted) {
    const nameRaw = row[nameKey];
    const name = typeof nameRaw === "string" ? nameRaw : String(nameRaw ?? "");
    const valRaw = row[valueKey];
    const num =
      typeof valRaw === "number"
        ? valRaw
        : typeof valRaw === "string"
          ? Number.parseFloat(valRaw)
          : Number(valRaw);
    if (name === "" || Number.isNaN(num)) continue;

    const customColor = colors?.[name];
    const fill = customColor ?? colorForLabel(name, paletteIndex);
    chartConfig[name] = {
      label: name,
      color: fill,
    };
    paletteIndex += 1;
    chartData.push({
      ...row,
      [nameKey]: name,
      [valueKey]: num,
      fill,
    } as PieDonutDatum);
  }

  return { chartConfig, chartData };
};
